import { X, MapPin, Loader2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { useLocationContext } from "../../contexts/locationContext";
import { handleGetCountries as fetchCountries } from "../../shared/controllers/generalController";
import { toIso3 } from "../../utils/locationStorage";
import SnackbarUtils from "../../utils/snackbarUtils";
import SearchSelect, { SearchSelectOption } from "../ui/SearchSelect";

const buttonBaseClasses =
    "inline-flex items-center gap-2 px-3 py-2 text-xs md:text-sm font-semibold font-Manrope whitespace-nowrap rounded-md border border-[#E4E7EC] text-[#344054] bg-white hover:bg-[#F9FAFB] transition-colors";

const pillClasses =
    "inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold font-Manrope whitespace-nowrap rounded-full bg-[#E9EDE9] text-[#1D2939]";

interface LocationSelectorProps {
    variant?: "button" | "pill";
    className?: string;
    buttonLabel?: string;
    externalOpenKey?: number;
}

interface CountryData {
    id: number | string;
    name: string;
    code?: string;
    iso3?: string;
    iso_3?: string;
    iso2?: string;
    iso_2?: string;
    [key: string]: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const toCountryData = (value: unknown): CountryData | null => {
    if (!isRecord(value)) {
        return null;
    }

    const id = value.id;
    const name = value.name;
    if ((typeof id !== "string" && typeof id !== "number") || typeof name !== "string") {
        return null;
    }

    return value as CountryData;
};

const LocationSelector: React.FC<LocationSelectorProps> = ({
    variant = "button",
    className = "",
    buttonLabel,
    externalOpenKey,
}) => {
    const { location, updateLocation } = useLocationContext();

    const [isOpen, setIsOpen] = useState(false);

    // Open modal when externalOpenKey changes (programmatic open)
    useEffect(() => {
        if (externalOpenKey) {
            setIsOpen(true);
        }
    }, [externalOpenKey]);

    const [isSaving, setIsSaving] = useState(false);
    const [countries, setCountries] = useState<CountryData[]>([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [selection, setSelection] = useState({
        countryId: "" as string | number,
        countryCode: "",
        countryName: "",
    });

    const countryOptions: SearchSelectOption[] = useMemo(
        () =>
            countries.map((country) => ({
                value: String(country.id),
                label: country.name,
                data: country,
            })),
        [countries]
    );

    useEffect(() => {
        if (!isOpen || countries.length) {
            return;
        }

        const load = async () => {
            setLoadingCountries(true);
            try {
                const result = await fetchCountries();
                const data = Array.isArray(result?.data) ? result.data : (Array.isArray(result) ? result : []);
                setCountries(data);
            } catch (error: unknown) {
                const message =
                    typeof error === "object" &&
                    error !== null &&
                    "message" in error &&
                    typeof (error as { message?: unknown }).message === "string"
                        ? (error as { message: string }).message
                        : "Failed to load countries";
                SnackbarUtils.error(message);
            } finally {
                setLoadingCountries(false);
            }
        };

        load();
    }, [isOpen, countries.length]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setSelection({
            countryId: "",
            countryCode: location.countryCode || "",
            countryName: location.countryName || "",
        });
    }, [isOpen, location]);

    useEffect(() => {
        if (
            !isOpen ||
            !selection.countryCode ||
            selection.countryId ||
            !countries.length
        ) {
            return;
        }

        const matched = countries.find((country) => {
            const candidates = [
                country.iso3,
                country.iso_3,
                country.code,
                country.iso2,
                country.iso_2,
            ]
                .filter(Boolean)
                .map((value) => toIso3(value));
            return candidates.includes(selection.countryCode);
        });
        if (matched) {
            handleCountryChange(matched);
        }
    }, [isOpen, countries, selection.countryCode, selection.countryId]);

    const handleCountryChange = (country: CountryData | null) => {
        if (!country) {
            setSelection((prev) => ({
                ...prev,
                countryId: "",
                countryCode: "",
                countryName: "",
            }));
            return;
        }

        const normalizedCode = toIso3(
            country.iso3 ||
            country.iso_3 ||
            country.code ||
            country.iso2 ||
            country.iso_2 ||
            ""
        );

        setSelection((prev) => ({
            ...prev,
            countryId: country.id,
            countryCode: normalizedCode,
            countryName: country.name,
        }));
    };

    const handleSave = async () => {
        if (!selection.countryCode) {
            SnackbarUtils.error("Select a country to continue");
            return;
        }

        setIsSaving(true);

        try {
            // Use the context's updateLocation method which handles both local state and backend updates
            await updateLocation({
                countryCode: selection.countryCode,
                countryName: selection.countryName,
                state: "",
                city: "",
            });

            // Show success message
            const countryName = selection.countryName || selection.countryCode;
            SnackbarUtils.success(`Location updated to ${countryName}`);

            setIsOpen(false);
        } catch (error: unknown) {
            const message =
                typeof error === "object" &&
                error !== null &&
                "message" in error &&
                typeof (error as { message?: unknown }).message === "string"
                    ? (error as { message: string }).message
                    : "Failed to update country";
            SnackbarUtils.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    const getButtonDisplayText = () => {
        if (buttonLabel) return buttonLabel;

        const text =
            location.countryName ||
            location.countryCode ||
            "Choose location";

        // Truncate if the text is too long
        return text.length > 20 ? `${text.substring(0, 18)}...` : text;
    };

    const buttonText = getButtonDisplayText();

    const buttonClass = `${variant === "pill" ? pillClasses : buttonBaseClasses
        } ${className}`.trim();

    return (
        <>
            <button
                type="button"
                className={buttonClass}
                onClick={() => setIsOpen(true)}
            >
                <MapPin size={16} className="text-[#317333]" />
                <span className="capitalize hidden lg:inline">{buttonText}</span>
            </button>

            {isOpen &&
                createPortal(
                    <div className="w-[100vw] h-full bg-[#1212128d] z-[99999] fixed top-0 left-0 p-6 flex justify-center items-center">
                        {" "}
                        <div className="bg-[#FFFFFF] p-6 rounded-[15px] w-full max-w-[600px] font-Manrope flex flex-col max-h-[95vh] min-h-[500px]">
                            <div className="w-full bg-[#fff]">
                                <div className="flex items-center justify-between pb-4 border-b border-[#E4E7EC]">
                                    <div>
                                        <p className="text-base font-semibold text-[#101828]">
                                            Choose your location
                                        </p>
                                        <p className="text-xs text-[#667085]">
                                            We use your region to show relevant games, pitches, and
                                            shop items.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setIsOpen(false)}
                                        className="p-1 rounded-full hover:bg-[#F2F4F7]"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>

                                <div className="py-5 space-y-4 overflow-y-auto max-h-[calc(95vh-200px)] min-h-[300px]">
                                    <label className="flex flex-col text-sm font-semibold text-[#101828]">
                                        Country
                                        <div className="mt-2">
                                            <SearchSelect
                                                options={countryOptions}
                                                value={selection.countryId ? String(selection.countryId) : ""}
                                                onChange={(option) => handleCountryChange(toCountryData(option?.data))}
                                                placeholder={loadingCountries ? "Loading countries..." : "Select country"}
                                                disabled={loadingCountries}
                                                loading={loadingCountries}
                                            />
                                        </div>
                                    </label>

                                </div>

                                <div className="py-4 border-t border-[#E4E7EC] flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        className="px-4 py-2 text-sm font-semibold text-[#475467] border border-[#DAE0E6] rounded-md"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="px-4 py-2 text-sm font-semibold rounded-md bg-[#317333] text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        onClick={handleSave}
                                        disabled={isSaving}
                                    >
                                        {isSaving && <Loader2 size={16} className="animate-spin" />}
                                        {isSaving ? "Saving..." : "Save"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default LocationSelector;
